import { type FC, memo, useCallback, useMemo, useState } from "react";
import { isNil } from "lodash-es";
import { css } from "@stubhub/broadway-foundation/css";
import { Accordion, AccordionContent, AccordionHeader, Heading } from "@stubhub/broadway-web";
import { AddFeature, RecordParticipationTypes, useSimpleFeatureTernary } from "@viagogo/feature";
import { Content, ContentPosition, ContentPositionType, useContent } from "src/shared/components/content";
import { RenderPositionalContent } from "src/shared/components/content/positional-content/PositionalContent";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { DataTestIds } from "src/shared/constants/TestEnums";
import { useInView } from "src/shared/hooks/useInView";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { useIsStubhub } from "src/shared/utils/appContextHelper";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { usePerformerSeoBottomContent } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerSeoBottomContent";
import { SeoInformationV2 } from "src/website-apps/shared/components/seo-content";
import { FaqAccordion } from "./FaqAccordion";
import { type SeoBodySplit, splitSeoBody } from "./parseFaqItems";
import { PerformerSeoTables } from "./PerformerSeoTables";

const stackGap = css({ display: "flex", flexDirection: "column", rowGap: "primitives.200" });

const emptySeoBodySplit: SeoBodySplit = Object.freeze({ prose: "", items: [], trailing: "" });

const flushAccordionHeader = css({
  "&[aria-expanded]": { paddingInlineStart: "primitives.0" },
  "&:hover": { backgroundColor: "[transparent]" },
  "&:active": { backgroundColor: "[transparent]" },
  // Toggle text, not content — matches Broadway's tabs recipe; answers stay selectable (sibling, not child).
  userSelect: "[none]",
});

// Broadway bakes the scale/height reveal and content inline padding into its Accordion recipe with no opt-out
// props — this instance toggles instantly and aligns with the page gutter.
const accordionContentFlush = css({
  "&[data-expanded]": {
    transitionProperty: "[none]",
    paddingInlineStart: "primitives.0",
    paddingInlineEnd: "primitives.0",
  },
});

interface Props {
  performerInfo: { hasEnoughData: boolean | null };
}

export const SeoBottomContent: FC<Props> = memo(({ performerInfo }) => {
  const model = usePerformerPageModelData();
  const isStubhub = useIsStubhub();

  const performerName = model.categorySummary?.categoryName ?? "";

  const [isExpanded, setIsExpanded] = useState(model.renderContext ?? false);
  // Bots must always receive the full subtree; isExpanded is user state and must not be able to hide crawler content.
  const contentExpanded = isExpanded || !!model.renderContext;

  // Broadway icon svgs share literal title/desc IDs, so the header chevron's aria-labelledby resolves to
  // the site header search icon's text; an explicit header label overrides that until the icon base is fixed.
  const aboutLabel = useContent("cid$#3715760#");
  const headerLabel = aboutLabel && performerName ? `${aboutLabel} ${performerName}` : undefined;

  // React 18 drops boolean inert, so AccordionContent's collapsed subtree stays tabbable — apply it
  // imperatively until the app is on React 19 (Broadway's inert={true} then works as-is). Callback ref,
  // not an isExpanded effect: deferred content mounts the accordion late, after the first commit.
  const collapsedContentRef = useCallback(
    (node: HTMLDivElement | null) => {
      node?.toggleAttribute("inert", !isExpanded);
    },
    [isExpanded],
  );

  const storePageEvent = useStorePageEvent();
  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
    storePageEvent(PE_TYPE.CLICK, PE_KEYS.PERFORMER_SEO_BOTTOM_CONTENT_ACCORDION_TOGGLE, "");
  }, [storePageEvent]);

  // Backend only serves the generic FAQ on StubHub, so viagogo users are ineligible either way.
  const canParticipateGenericFaq = useCallback(() => isStubhub, [isStubhub]);

  // Backend renders the event table for all non-major-league-sports performers (the else branch).
  // Participation mirrors that: eligible unless the performer is a major-league sports team
  // (which gets its own sports tables + separate participation).
  const canParticipateEventTable = useCallback(
    () => !(model.isMajorLeagueSportsTeam ?? false),
    [model.isMajorLeagueSportsTeam],
  );

  const canParticipateSportsTables = useCallback(
    () => model.isMajorLeagueSportsTeam ?? false,
    [model.isMajorLeagueSportsTeam],
  );

  // Variant render gates — record:false prevents double-recording with the AddFeature wrappers above.
  const isAssignedEventTableVariant =
    useSimpleFeatureTernary(FeatureSetEnum.TMPerformerSeoEventTableLazyLoad, false, true, undefined, false) ?? false;
  const isAssignedSportsTablesVariant =
    useSimpleFeatureTernary(FeatureSetEnum.TMPerformerSeoSportsTablesLazyLoad, false, true, undefined, false) ?? false;

  const rowsDeferred =
    isNil(model.performerEventTableRows) &&
    isNil(model.performerSportsScheduleTableRows) &&
    isNil(model.leagueTeamsTableRows);
  // Crawlers with no eligible rows also leave the rows null — never take the deferred branch for them.
  const showDeferredEventTable =
    rowsDeferred && !model.renderContext && isAssignedEventTableVariant && !(model.isMajorLeagueSportsTeam ?? false);
  const showDeferredSportsTables =
    rowsDeferred && !model.renderContext && isAssignedSportsTablesVariant && (model.isMajorLeagueSportsTeam ?? false);

  // Real users receive the whole SEO bottom content (bio, CMS body, FAQ, tables) from the deferred fetch;
  // bots get it eagerly in the model, so their bio/FAQ/CMS fields are already populated.
  const bottomContentDeferred =
    !model.renderContext &&
    isNil(model.performerDescriptionContent) &&
    !model.showCmsBottomContent &&
    !model.showGenericFaq &&
    !model.showTourFaq;

  // Observing the accordion wrapper (not a slot inside it) lets the fetch fire while
  // collapsed — an observer inside the clipped content cannot intersect until expansion.
  const { ref: accordionRef, inView: accordionNearViewport } = useInView({
    triggerOnce: true,
    rootMargin: "800px",
  });

  const shouldFetchDeferredContent =
    accordionNearViewport && (showDeferredEventTable || showDeferredSportsTables || bottomContentDeferred);
  const {
    data: fetchedContent,
    isPending: deferredContentPending,
    isSuccess: deferredContentSucceeded,
  } = usePerformerSeoBottomContent(shouldFetchDeferredContent);

  // Single source of bottom content: the model on the bot/SSR path, the deferred fetch on the real-user path.
  const descriptionContent = model.performerDescriptionContent ?? fetchedContent?.performerDescriptionContent;
  const showCmsBottomContent = model.showCmsBottomContent || (fetchedContent?.showCmsBottomContent ?? false);
  const cmsBottomContentBody = model.cmsBottomContentBody ?? fetchedContent?.cmsBottomContentBody;
  const showGenericFaq = model.showGenericFaq || (fetchedContent?.showGenericFaq ?? false);
  const showTourFaq = model.showTourFaq || (fetchedContent?.showTourFaq ?? false);
  const tourFaqBody = model.tourFaqBody ?? fetchedContent?.tourFaqBody;

  // Only pages carrying tour data can ever be shown the variant, and the backend resolves that without the arm —
  // so gating here keeps both arms recorded while leaving the visitor's variant unknown to the client.
  const isTourFaqEligible = fetchedContent?.isTourFaqEligible ?? false;
  const canParticipateTourFaq = useCallback(() => isStubhub && isTourFaqEligible, [isStubhub, isTourFaqEligible]);

  const eventTableRows = model.performerEventTableRows ?? fetchedContent?.performerEventTableRows;
  const sportsScheduleRows = model.performerSportsScheduleTableRows ?? fetchedContent?.performerSportsScheduleTableRows;
  const leagueTeamsRows = model.leagueTeamsTableRows ?? fetchedContent?.leagueTeamsTableRows;
  const hasEagerRows =
    !!model.performerEventTableRows?.length ||
    !!model.leagueTeamsTableRows?.length ||
    !!model.performerSportsScheduleTableRows?.length;

  // With the FAQ accordion on, the CMS body's Q&A collapses into its leading rows; prose above
  // (and any prose trailing the Q&A) stays expanded, and the Divider separates bio from FAQ.
  const cmsBodySplit = useMemo(
    () => (contentExpanded ? splitSeoBody(cmsBottomContentBody) : emptySeoBodySplit),
    [contentExpanded, cmsBottomContentBody],
  );
  const wrapCmsFaq = !!showGenericFaq && !!showCmsBottomContent && cmsBodySplit.items.length > 0;
  const cmsProse = wrapCmsFaq ? cmsBodySplit.prose : (cmsBottomContentBody ?? "");
  // Bio flag mirrors the render conditions: the description only renders when the CMS body is off,
  // and the CMS prose only renders when the body is on — neither may produce a stray Divider.
  const hasBio = (!!descriptionContent && !showCmsBottomContent) || (!!showCmsBottomContent && !!cmsProse);

  // Mirror cmsBodySplit: server-resolved tour FAQ body → parsed items passed to FaqAccordion.
  const tourFaqSplit = useMemo(
    () => (contentExpanded ? splitSeoBody(tourFaqBody) : emptySeoBodySplit),
    [contentExpanded, tourFaqBody],
  );
  const tourItems = showTourFaq && tourFaqSplit.items.length > 0 ? tourFaqSplit.items : undefined;
  // Leading prose renders above the Q&A; a body that no longer parses as Q&A renders raw instead of silently dropping the section.
  const tourProse = showTourFaq ? (tourItems ? tourFaqSplit.prose : (tourFaqBody ?? "")) : "";

  const hasAnyContent =
    (!showCmsBottomContent && !!descriptionContent) ||
    (!!showCmsBottomContent && !!cmsBottomContentBody) ||
    !!showGenericFaq ||
    !!showTourFaq ||
    !!model.performerEventTableRows?.length ||
    !!model.leagueTeamsTableRows?.length ||
    !!model.performerSportsScheduleTableRows?.length;

  // A resolved-empty fetch drops the whole accordion only when no bio/FAQ/SSR content exists
  // (eager-SSR parity). Gate on success, not !isPending: a failed fetch also leaves isPending
  // false with no data, and must not be mistaken for a resolved-empty response.
  const removeEmptyAccordion =
    !hasAnyContent &&
    shouldFetchDeferredContent &&
    deferredContentSucceeded &&
    !fetchedContent?.performerEventTableRows?.length &&
    !fetchedContent?.performerSportsScheduleTableRows?.length &&
    !fetchedContent?.leagueTeamsTableRows?.length &&
    isNil(fetchedContent?.performerDescriptionContent) &&
    !fetchedContent?.showCmsBottomContent &&
    !fetchedContent?.showGenericFaq &&
    !fetchedContent?.showTourFaq;

  return (
    <>
      <AddFeature
        // just for participation recording purposes
        id={FeatureSetEnum.TMPerformerRemoveBottomBioGating}
        canParticipate={() => !!performerInfo.hasEnoughData}
        recordType={RecordParticipationTypes.IN_VIEW}
        feature={() => null}
      />

      {/* Participation only: rendering stays backend-driven so the isBot override can force the FAQ on for crawlers. */}
      <AddFeature
        id={FeatureSetEnum.TMPerformerGenericFAQBottomSection}
        canParticipate={canParticipateGenericFaq}
        recordType={RecordParticipationTypes.IN_VIEW}
        feature={() => null}
      />

      {/* Participation only: same backend-driven rendering contract as the generic FAQ — this arm only controls the tour rows. */}
      <AddFeature
        id={FeatureSetEnum.TMPerformerTourFaqBottomSection}
        canParticipate={canParticipateTourFaq}
        recordType={RecordParticipationTypes.IN_VIEW}
        feature={() => null}
      />

      {/* Participation only: rendering stays backend-driven so the isBot override can force the table on for crawlers. */}
      <AddFeature
        id={FeatureSetEnum.TMPerformerSeoEventTableLazyLoad}
        canParticipate={canParticipateEventTable}
        recordType={RecordParticipationTypes.IN_VIEW}
        feature={() => null}
      />

      {/* Participation only: rendering stays backend-driven so the isBot override can force the tables on for crawlers. */}
      <AddFeature
        id={FeatureSetEnum.TMPerformerSeoSportsTablesLazyLoad}
        canParticipate={canParticipateSportsTables}
        recordType={RecordParticipationTypes.IN_VIEW}
        feature={() => null}
      />

      {/* Participation must record for both arms regardless of content presence — gating on hasAnyContent would
          skew control vs variant on bio-less performers (the failure mode that invalidated FAQ V1, 103280). */}
      {/* The sentinel stays mounted while the bottom content is deferred so the proximity observer has a target;
          the accordion itself mounts only once content exists (or a table variant is active). */}
      <div ref={accordionRef} />

      {(hasAnyContent || showDeferredEventTable || showDeferredSportsTables) && !removeEmptyAccordion && (
        <div>
          <Accordion expanded={isExpanded} onToggle={handleToggle}>
            <AccordionHeader aria-label={headerLabel} classes={{ root: flushAccordionHeader }}>
              <Heading as="h2" variant="header">
                {/* "About" */}
                <Content id="cid$#3715760#" />
                {performerName && ` ${performerName}`}
              </Heading>
            </AccordionHeader>
            <div ref={collapsedContentRef} data-testid={DataTestIds.SeoBottomContentCollapsedWrapper}>
              <AccordionContent className={accordionContentFlush}>
                {/* Prefetch the bio while collapsed so expanding never waits on a new request; renders nothing. */}
                {!contentExpanded && !showCmsBottomContent && !!descriptionContent && (
                  <RenderPositionalContent
                    id={model.categorySummary.categoryId}
                    type={ContentPositionType.Experimental}
                    position={ContentPosition.StubhubPerformerSeoBottomContent}
                  >
                    {() => null}
                  </RenderPositionalContent>
                )}
                {contentExpanded && (
                  <div className={stackGap}>
                    {/* Productionized: TMPerformerDataSteppingStone (TM:17242, Deployed Variant 1) */}
                    {!showCmsBottomContent && !!descriptionContent && (
                      <RenderPositionalContent
                        id={model.categorySummary.categoryId}
                        type={ContentPositionType.Experimental}
                        position={ContentPosition.StubhubPerformerSeoBottomContent}
                      >
                        {(content) => (
                          <SeoInformationV2
                            content={content}
                            includeTitle={descriptionContent?.includeTitle}
                            includeAgnosticText={false}
                            title={model.categorySummary.categoryTitle}
                            transparent
                            flushTop
                            flushBottom
                          />
                        )}
                      </RenderPositionalContent>
                    )}

                    {/* CMS prose, CMS Q&A items, and the generic FAQ are kept in normal flow so Q&A margins
                        collapse instead of stacking with the flex gap. */}
                    {(!!showCmsBottomContent || !!showGenericFaq || !!showTourFaq) && (
                      <div>
                        {!!showCmsBottomContent && !!cmsProse && (
                          <SeoInformationV2
                            content={cmsProse}
                            title={model.categorySummary.categoryTitle}
                            includeTitle={false}
                            includeAgnosticText={false}
                            transparent
                            showBackToTop={!showGenericFaq}
                            flushTop
                            flushBottom
                          />
                        )}

                        {!!tourProse && (
                          <SeoInformationV2
                            content={tourProse}
                            title={model.categorySummary.categoryTitle}
                            includeTitle={false}
                            includeAgnosticText={false}
                            transparent
                            flushTop
                            flushBottom
                          />
                        )}

                        {(!!showGenericFaq || !!showTourFaq) && (
                          <FaqAccordion
                            hasBio={hasBio}
                            expandAll={!!model.renderContext}
                            performerItems={wrapCmsFaq ? cmsBodySplit.items : undefined}
                            showGenericItems={!!showGenericFaq}
                            {...(tourItems ? { tourItems } : {})}
                          />
                        )}

                        {tourItems && !!tourFaqSplit.trailing && (
                          <SeoInformationV2
                            content={tourFaqSplit.trailing}
                            title={model.categorySummary.categoryTitle}
                            includeTitle={false}
                            includeAgnosticText={false}
                            transparent
                            flushTop
                            flushBottom
                          />
                        )}

                        {wrapCmsFaq && !!cmsBodySplit.trailing && (
                          <SeoInformationV2
                            content={cmsBodySplit.trailing}
                            title={model.categorySummary.categoryTitle}
                            includeTitle={false}
                            includeAgnosticText={false}
                            transparent
                            flushTop
                            flushBottom
                          />
                        )}
                      </div>
                    )}

                    {/* Bot/SSR path: model rows present, render directly. Variant path: mounted only when
                        expanded; the fetch started at accordion proximity, so this renders from cache
                        (skeleton only if the fetch is still in flight). */}
                    {(hasEagerRows || ((showDeferredEventTable || showDeferredSportsTables) && isExpanded)) && (
                      <PerformerSeoTables
                        eventTableRows={eventTableRows}
                        sportsScheduleRows={sportsScheduleRows}
                        leagueTeamsRows={leagueTeamsRows}
                        performerName={performerName}
                        isMajorLeagueSportsTeam={!!model.isMajorLeagueSportsTeam}
                        isPending={deferredContentPending}
                      />
                    )}
                  </div>
                )}
              </AccordionContent>
            </div>
          </Accordion>
        </div>
      )}
    </>
  );
});

SeoBottomContent.displayName = "SeoBottomContent";
